import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PdfCanvasPreview } from "@/components/settings/PdfCanvasPreview";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Trash2,
  Calendar,
  Building2,
  PenLine,
  FileText,
  FileDown,
  Loader2,
  Pencil,
  Save,
  X,
  Send,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  Eye,
  EyeOff,
} from "lucide-react";
import { isSectionHidden } from "@/lib/contract-sections";
import { generateContractPdf } from "@/utils/reports.functions";
import { sendContractApproval } from "@/utils/contracts.functions";
import { downloadPdfFromBase64 } from "@/lib/report-export";
import { EmailSendHistoryCard } from "@/components/estimates/EmailSendHistoryCard";

export const Route = createFileRoute("/_app/contracts/$contractId")({
  component: ContractDetailPage,
});

type Status = "draft" | "sent" | "signed" | "active" | "completed" | "cancelled";

type Contract = {
  id: string;
  number: string;
  title: string;
  scope: string | null;
  terms: string | null;
  notes: string | null;
  inspections: string | null;
  insurance_liability: string | null;
  materials_workmanship: string | null;
  cleanup: string | null;
  completion_terms: string | null;
  agreement_text: string | null;
  hidden_sections: string[] | null;
  status: Status;
  amount: number;
  currency: string;
  retainage_percent: number;
  start_date: string | null;
  end_date: string | null;
  signed_at: string | null;
  client_signature_name: string | null;
  client_signature_email: string | null;
  company_signature_name: string | null;
  client_id: string;
  estimate_id: string | null;
  clients: { name: string; email: string | null } | null;
  estimates: { number: string } | null;
};

type ApprovalToken = {
  id: string;
  token: string;
  status: "sent" | "approved" | "rejected" | "revoked";
  client_email: string | null;
  created_at: string;
  responded_at: string | null;
  response_notes: string | null;
  client_signature_data: string | null;
  client_signature_name: string | null;
};

const statusVariant: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "secondary",
  sent: "outline",
  signed: "default",
  active: "default",
  completed: "secondary",
  cancelled: "destructive",
};

function ContractDetailPage() {
  const { contractId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const [contract, setContract] = useState<Contract | null>(null);
  const [tokens, setTokens] = useState<ApprovalToken[]>([]);
  const [approvalLinksOpen, setApprovalLinksOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signName, setSignName] = useState("");
  const [signEmail, setSignEmail] = useState("");
  const [signing, setSigning] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string>("");

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editAmount, setEditAmount] = useState("0");
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [editScope, setEditScope] = useState("");
  const [editTerms, setEditTerms] = useState("");
  const [editInspections, setEditInspections] = useState("");
  const [editInsurance, setEditInsurance] = useState("");
  const [editMaterials, setEditMaterials] = useState("");
  const [editCleanup, setEditCleanup] = useState("");
  const [editCompletion, setEditCompletion] = useState("");
  const [editAgreement, setEditAgreement] = useState("");
  const [editHidden, setEditHidden] = useState<Set<string>>(new Set());

  const toggleSection = (key: string) =>
    setEditHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderSectionLabel = (key: string, label: string) => {
    const hidden = editHidden.has(key);
    return (
      <div className="flex items-center justify-between gap-2">
        <Label className={hidden ? "text-muted-foreground line-through" : ""}>{label}</Label>
        <button
          type="button"
          onClick={() => toggleSection(key)}
          className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition-colors ${
            hidden
              ? "text-amber-600 hover:text-amber-700"
              : "text-muted-foreground hover:text-foreground"
          }`}
          title={
            hidden
              ? t("pages.contracts.detail.sectionShowTitle")
              : t("pages.contracts.detail.sectionHideTitle")
          }
        >
          {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {hidden
            ? t("pages.contracts.detail.sectionHidden")
            : t("pages.contracts.detail.sectionVisible")}
        </button>
      </div>
    );
  };

  // Send approval
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendMessage, setSendMessage] = useState("");
  const [sending, setSending] = useState(false);

  const isAdmin = hasRole("admin");
  const generateContractPdfFn = useServerFn(generateContractPdf);
  const sendContractApprovalFn = useServerFn(sendContractApproval);

  const openPdfPreview = async () => {
    if (!contract) return;
    setDownloadingPdf(true);
    try {
      const { pdfBase64, filename } = await generateContractPdfFn({
        data: { contractId: contract.id },
      });
      setPdfPreviewBase64(pdfBase64);
      setPdfPreviewFilename(filename || `contrato-${contract.number}.pdf`);
      setPdfPreviewOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.contracts.detail.pdfGenerateFailed"));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const confirmDownloadPdf = () => {
    if (!pdfPreviewBase64) return;
    downloadPdfFromBase64(
      pdfPreviewFilename || `contrato-${contract?.number ?? "contract"}.pdf`,
      pdfPreviewBase64,
    );
    toast.success(t("pages.contracts.detail.pdfDownloaded"));
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPdfPreviewBase64(null);
  };

  const load = async () => {
    setLoading(true);
    const [{ data, error }, { data: tks }] = await Promise.all([
      supabase
        .from("contracts")
        .select("*, clients(name, email), estimates(number)")
        .eq("id", contractId)
        .maybeSingle(),
      supabase
        .from("contract_approval_tokens")
        .select(
          "id, token, status, client_email, created_at, responded_at, response_notes, client_signature_data, client_signature_name",
        )
        .eq("contract_id", contractId)
        .order("created_at", { ascending: false }),
    ]);
    if (error) toast.error(t("pages.contracts.detail.loadFailed"), { description: error.message });
    const c = (data ?? null) as Contract | null;
    setContract(c);
    setTokens((tks ?? []) as ApprovalToken[]);
    if (c?.clients) {
      setSignName(c.client_signature_name ?? c.clients?.name ?? "");
      setSignEmail(c.client_signature_email ?? c.clients?.email ?? "");
    }
    if (c) {
      setEditTitle(c.title);
      setEditAmount(String(c.amount ?? 0));
      setEditStart(c.start_date ?? "");
      setEditEnd(c.end_date ?? "");
      setEditScope(c.scope ?? "");
      setEditTerms(c.terms ?? "");
      setEditInspections(
        (c.inspections ?? "") || t("pages.contracts.detail.clauseDefaults.inspections"),
      );
      setEditInsurance(
        (c.insurance_liability ?? "") || t("pages.contracts.detail.clauseDefaults.insurance"),
      );
      setEditMaterials(
        (c.materials_workmanship ?? "") || t("pages.contracts.detail.clauseDefaults.materials"),
      );
      setEditCleanup((c.cleanup ?? "") || t("pages.contracts.detail.clauseDefaults.cleanup"));
      setEditCompletion(c.completion_terms ?? "");
      setEditAgreement(
        (c.agreement_text ?? "") || t("pages.contracts.detail.clauseDefaults.agreement"),
      );
      setEditHidden(new Set((c.hidden_sections ?? []) as string[]));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const updateStatus = async (status: Status) => {
    if (!contract) return;
    const { error } = await supabase.from("contracts").update({ status }).eq("id", contract.id);
    if (error)
      toast.error(t("pages.contracts.detail.statusFailed"), { description: error.message });
    else {
      setContract({ ...contract, status });
      toast.success(t("pages.contracts.detail.statusUpdated", { status: t(`statuses.${status}`) }));
    }
  };

  const sign = async () => {
    if (!contract) return;
    if (!signName.trim()) return toast.error(t("pages.contracts.detail.signerNameRequired"));
    setSigning(true);
    const { error } = await supabase
      .from("contracts")
      .update({
        client_signature_name: signName.trim(),
        client_signature_email: signEmail.trim() || null,
        signed_at: new Date().toISOString(),
        status: "signed",
      })
      .eq("id", contract.id);
    setSigning(false);
    if (error) {
      toast.error(t("pages.contracts.detail.signFailed"), { description: error.message });
    } else {
      toast.success(t("pages.contracts.detail.contractSigned"));
      setSignOpen(false);
      load();
    }
  };

  const onDelete = async () => {
    if (!contract) return;
    const { error } = await supabase.from("contracts").delete().eq("id", contract.id);
    if (error)
      toast.error(t("pages.contracts.detail.deleteFailed"), { description: error.message });
    else {
      toast.success(t("pages.contracts.detail.deleted"));
      navigate({ to: "/contracts" });
    }
  };

  const cancelEdit = () => {
    if (!contract) return;
    setEditTitle(contract.title);
    setEditAmount(String(contract.amount ?? 0));
    setEditStart(contract.start_date ?? "");
    setEditEnd(contract.end_date ?? "");
    setEditScope(contract.scope ?? "");
    setEditTerms(contract.terms ?? "");
    setEditInspections(
      (contract.inspections ?? "") || t("pages.contracts.detail.clauseDefaults.inspections"),
    );
    setEditInsurance(
      (contract.insurance_liability ?? "") || t("pages.contracts.detail.clauseDefaults.insurance"),
    );
    setEditMaterials(
      (contract.materials_workmanship ?? "") ||
        t("pages.contracts.detail.clauseDefaults.materials"),
    );
    setEditCleanup((contract.cleanup ?? "") || t("pages.contracts.detail.clauseDefaults.cleanup"));
    setEditCompletion(contract.completion_terms ?? "");
    setEditAgreement(
      (contract.agreement_text ?? "") || t("pages.contracts.detail.clauseDefaults.agreement"),
    );
    setEditHidden(new Set((contract.hidden_sections ?? []) as string[]));
    setEditing(false);
  };

  const performSaveEdit = async (resetToDraft: boolean) => {
    if (!contract) return;
    if (!editTitle.trim()) {
      toast.error(t("pages.contracts.form.errorTitle"));
      return;
    }
    setSavingEdit(true);
    try {
      const update: {
        title: string;
        amount: number;
        start_date: string | null;
        end_date: string | null;
        scope: string | null;
        terms: string | null;
        inspections: string | null;
        insurance_liability: string | null;
        materials_workmanship: string | null;
        cleanup: string | null;
        completion_terms: string | null;
        agreement_text: string | null;
        hidden_sections: string[];
        status?: Status;
        signed_at?: string | null;
      } = {
        title: editTitle.trim(),
        amount: Number(editAmount) || 0,
        start_date: editStart || null,
        end_date: editEnd || null,
        scope: editScope.trim() || null,
        terms: editTerms.trim() || null,
        inspections: editInspections.trim() || null,
        insurance_liability: editInsurance.trim() || null,
        materials_workmanship: editMaterials.trim() || null,
        cleanup: editCleanup.trim() || null,
        completion_terms: editCompletion.trim() || null,
        agreement_text: editAgreement.trim() || null,
        hidden_sections: [...editHidden],
      };
      if (resetToDraft) {
        update.status = "draft";
        update.signed_at = null;
      }
      const { error } = await supabase.from("contracts").update(update).eq("id", contract.id);
      if (error) throw error;

      if (resetToDraft) {
        const { error: rErr } = await supabase
          .from("contract_approval_tokens")
          .update({ status: "revoked" })
          .eq("contract_id", contract.id)
          .eq("status", "sent");
        if (rErr) console.error("Could not revoke pending tokens", rErr);
      }

      toast.success(t("pages.contracts.detail.saveSuccess"));
      setEditing(false);
      setConfirmReset(false);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : t("pages.contracts.detail.saveFailed");
      toast.error(t("pages.contracts.detail.saveFailed"), { description: msg });
    } finally {
      setSavingEdit(false);
    }
  };

  const saveEdit = () => {
    if (!contract) return;
    if (contract.status !== "draft") {
      setConfirmReset(true);
    } else {
      performSaveEdit(false);
    }
  };

  const openSendDialog = () => {
    setSendEmail(contract?.clients?.email ?? "");
    setSendMessage("");
    setSendOpen(true);
  };

  const sendApprovalLink = async () => {
    if (!contract) return;
    const email = sendEmail.trim();
    if (!email) {
      toast.error(t("pages.contracts.detail.recipientRequired"));
      return;
    }
    setSending(true);
    try {
      // 1) Generate the PDF and upload to public storage so the client can
      //    download it from the email.
      let pdfPublicUrl: string | undefined;
      try {
        const { pdfBase64, filename } = await generateContractPdfFn({
          data: { contractId: contract.id },
        });
        const bin = atob(pdfBase64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const safeName = (filename || `contract-${contract.number}.pdf`).replace(
          /[^a-zA-Z0-9._-]+/g,
          "-",
        );
        const storagePath = `contracts/${contract.id}/${Date.now()}-${safeName}`;
        const { error: upErr } = await supabase.storage
          .from("document-pdfs")
          .upload(storagePath, new Blob([bytes], { type: "application/pdf" }), {
            contentType: "application/pdf",
            upsert: true,
          });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from("document-pdfs").getPublicUrl(storagePath);
        pdfPublicUrl = pub?.publicUrl;
      } catch (pdfErr) {
        // Don't block sending the link if PDF generation/upload fails.
        console.error("Contract PDF generation/upload failed", pdfErr);
      }

      const result = await sendContractApprovalFn({
        data: {
          contractId: contract.id,
          recipientEmail: email,
          message: sendMessage.trim() || undefined,
          appOrigin: typeof window !== "undefined" ? window.location.origin : undefined,
          pdfUrl: pdfPublicUrl,
        },
      });
      toast.success(t("pages.contracts.detail.linkSent"), {
        description: result?.approval_url
          ? t("pages.contracts.detail.linkSentDescQueuedCopied")
          : t("pages.contracts.detail.linkSentDescQueued"),
      });
      if (result?.approval_url && typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(result.approval_url).catch(() => {});
      }
      setSendOpen(false);
      load();
    } catch (e) {
      toast.error(t("pages.contracts.detail.sendFailed"), {
        description: e instanceof Error ? e.message : t("common.error"),
      });
    } finally {
      setSending(false);
    }
  };

  const copyApprovalLink = async (token: string) => {
    const url = `${window.location.origin}/contracts/approve/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("pages.contracts.detail.copyLinkSuccess"));
    } catch {
      toast.error(t("pages.contracts.detail.copyLinkFailed"), { description: url });
    }
  };

  const revokeToken = async (id: string) => {
    const { error } = await supabase
      .from("contract_approval_tokens")
      .update({ status: "revoked" })
      .eq("id", id);
    if (error) {
      toast.error(t("pages.contracts.detail.revokeFailed"), { description: error.message });
    } else {
      toast.success(t("pages.contracts.detail.linkRevoked"));
      load();
    }
  };

  const formatMoney = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: contract?.currency || "USD",
    }).format(Number(v) || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="rounded-lg border border-border bg-card p-12 text-center">
        <p className="font-medium">{t("pages.contracts.detail.notFound")}</p>
        <Link to="/contracts" className="mt-2 inline-block text-sm text-primary hover:underline">
          {t("pages.contracts.detail.back")}
        </Link>
      </div>
    );
  }

  const isSigned = !!contract.signed_at;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/contracts" })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{contract.number}</span>
              <Badge variant={statusVariant[contract.status]}>
                {t(`statuses.${contract.status}`)}
              </Badge>
              {contract.estimates?.number && (
                <Badge variant="outline" className="gap-1 font-mono text-[10px]">
                  <FileText className="h-3 w-3" />
                  {contract.estimates.number}
                </Badge>
              )}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">{contract.title}</h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={openPdfPreview}
            disabled={downloadingPdf}
            className="gap-2"
          >
            <FileDown className="h-4 w-4" />
            {downloadingPdf
              ? t("pages.contracts.detail.generatingPdf")
              : t("pages.contracts.detail.viewPdf")}
          </Button>
          {!editing && contract.status !== "completed" && contract.status !== "cancelled" && (
            <Button variant="outline" onClick={() => setEditing(true)} className="gap-2">
              <Pencil className="h-4 w-4" />
              {t("pages.contracts.detail.edit")}
            </Button>
          )}
          {editing && (
            <>
              <Button onClick={saveEdit} disabled={savingEdit} className="gap-2">
                {savingEdit ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {savingEdit
                  ? t("pages.contracts.detail.savingDots")
                  : t("pages.contracts.detail.saveChanges")}
              </Button>
              <Button variant="ghost" onClick={cancelEdit} disabled={savingEdit} className="gap-2">
                <X className="h-4 w-4" />
                {t("common.cancel")}
              </Button>
            </>
          )}
          {!editing && contract.status !== "completed" && contract.status !== "cancelled" && (
            <Button onClick={openSendDialog} className="gap-2">
              <Send className="h-4 w-4" />
              {t("pages.contracts.detail.sendForApproval")}
            </Button>
          )}
          {!editing && !isSigned && (
            <Button variant="outline" onClick={() => setSignOpen(true)} className="gap-2">
              <PenLine className="h-4 w-4" />
              {t("pages.contracts.detail.recordSignature")}
            </Button>
          )}
          {!editing && (
            <Select value={contract.status} onValueChange={(v) => updateStatus(v as Status)}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">{t("statuses.draft")}</SelectItem>
                <SelectItem value="sent">{t("statuses.sent")}</SelectItem>
                <SelectItem value="signed">{t("statuses.signed")}</SelectItem>
                <SelectItem value="active">{t("statuses.active")}</SelectItem>
                <SelectItem value="completed">{t("statuses.completed")}</SelectItem>
                <SelectItem value="cancelled">{t("statuses.cancelled")}</SelectItem>
              </SelectContent>
            </Select>
          )}
          {!editing && isAdmin && (
            <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      </div>

      {contract.status !== "completed" && contract.status !== "cancelled" && (
        <div className="rounded-lg border border-border bg-card">
          <button
            type="button"
            onClick={() => setApprovalLinksOpen((o) => !o)}
            className="flex w-full items-center justify-between border-b border-border p-4 text-left hover:bg-muted/40"
            aria-expanded={approvalLinksOpen}
          >
            <p className="text-sm font-semibold">{t("pages.contracts.detail.approvalLinks")}</p>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${approvalLinksOpen ? "rotate-180" : ""}`}
            />
          </button>
          {approvalLinksOpen && (
            <>
              {tokens.length === 0 ? (
                <div className="flex flex-col items-start gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    {t("pages.contracts.detail.noApprovalLinksYet", {
                      defaultValue: "No approval link has been sent yet.",
                    })}
                  </p>
                  <Button size="sm" onClick={openSendDialog} className="gap-2">
                    <Send className="h-3.5 w-3.5" />
                    {t("pages.contracts.detail.sendForApproval")}
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {tokens.map((tk) => (
                    <div
                      key={tk.id}
                      className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-start gap-3">
                        {tk.status === "approved" ? (
                          <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
                        ) : tk.status === "rejected" ? (
                          <XCircle className="mt-0.5 h-4 w-4 text-destructive" />
                        ) : (
                          <Clock className="mt-0.5 h-4 w-4 text-muted-foreground" />
                        )}
                        <div>
                          <p className="text-sm font-medium capitalize">
                            {tk.status === "sent"
                              ? t("pages.contracts.detail.awaitingResponse")
                              : t(`statuses.${tk.status}`)}
                            {tk.client_email && (
                              <span className="ml-2 font-normal text-muted-foreground">
                                · {tk.client_email}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t("pages.contracts.detail.sentOn", {
                              date: new Date(tk.created_at).toLocaleString(),
                            })}
                            {tk.responded_at &&
                              t("pages.contracts.detail.respondedOn", {
                                date: new Date(tk.responded_at).toLocaleString(),
                              })}
                          </p>
                          {tk.response_notes && (
                            <p className="mt-1 text-sm whitespace-pre-wrap text-muted-foreground">
                              “{tk.response_notes}”
                            </p>
                          )}
                          {tk.status === "approved" && !tk.client_signature_data && (
                            <p className="mt-1 text-xs font-medium text-destructive">
                              {t("pages.contracts.detail.approvedInvalidNoSignature")}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {tk.status === "approved" && tk.client_signature_data && (
                          <Button
                            variant="default"
                            size="sm"
                            className="gap-2"
                            onClick={() =>
                              window.open(
                                `${window.location.origin}/contracts/approve/${tk.token}`,
                                "_blank",
                              )
                            }
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {t("pages.contracts.detail.viewSigned")}
                          </Button>
                        )}
                        {tk.status === "sent" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              onClick={() => copyApprovalLink(tk.token)}
                            >
                              <Copy className="h-3.5 w-3.5" />{" "}
                              {t("pages.contracts.detail.copyLink")}
                            </Button>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" onClick={() => revokeToken(tk.id)}>
                                {t("pages.contracts.detail.revoke")}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <EmailSendHistoryCard kind="contract" contractId={contract.id} />

      {editing ? (
        <div className="grid gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-2">
          {contract.status !== "draft" && (
            <div className="sm:col-span-2 rounded-md border border-border bg-muted p-3 text-xs text-muted-foreground">
              {t("pages.contracts.detail.editingWarn", {
                status: t(`statuses.${contract.status}`),
              })}
            </div>
          )}
          <div className="sm:col-span-2 rounded-md border border-dashed border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            {t("pages.contracts.detail.hideHint")}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("pages.contracts.detail.editTitleLabel")}</Label>
            <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            {renderSectionLabel("payment", t("pages.contracts.detail.editAmountLabel"))}
            <Input
              type="number"
              step="0.01"
              value={editAmount}
              onChange={(e) => setEditAmount(e.target.value)}
              className={editHidden.has("payment") ? "opacity-60" : ""}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("pages.contracts.detail.editStartLabel")}</Label>
              <Input type="date" value={editStart} onChange={(e) => setEditStart(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.contracts.detail.editEndLabel")}</Label>
              <Input type="date" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            {renderSectionLabel("scope", t("pages.contracts.detail.editScopeLabel"))}
            <Textarea
              value={editScope}
              onChange={(e) => setEditScope(e.target.value)}
              rows={6}
              className={editHidden.has("scope") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            {renderSectionLabel("payment", t("pages.contracts.detail.editTermsLabel"))}
            <Textarea
              value={editTerms}
              onChange={(e) => setEditTerms(e.target.value)}
              rows={5}
              className={editHidden.has("payment") ? "opacity-60" : ""}
            />
            <p className="text-xs text-muted-foreground">
              {t("pages.contracts.detail.paymentToggleHint")}
            </p>
          </div>

          <p className="sm:col-span-2 text-xs text-muted-foreground mt-2">
            {t("pages.contracts.detail.clauseHint")}
          </p>

          <div className="space-y-1.5 sm:col-span-2">
            {renderSectionLabel("inspections", t("pages.contracts.detail.editInspectionsLabel"))}
            <Textarea
              value={editInspections}
              onChange={(e) => setEditInspections(e.target.value)}
              rows={3}
              className={editHidden.has("inspections") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            {renderSectionLabel(
              "insurance_liability",
              t("pages.contracts.detail.editInsuranceLabel"),
            )}
            <Textarea
              value={editInsurance}
              onChange={(e) => setEditInsurance(e.target.value)}
              rows={3}
              className={editHidden.has("insurance_liability") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            {renderSectionLabel(
              "materials_workmanship",
              t("pages.contracts.detail.editMaterialsLabel"),
            )}
            <Textarea
              value={editMaterials}
              onChange={(e) => setEditMaterials(e.target.value)}
              rows={3}
              className={editHidden.has("materials_workmanship") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            {renderSectionLabel("cleanup", t("pages.contracts.detail.editCleanupLabel"))}
            <Textarea
              value={editCleanup}
              onChange={(e) => setEditCleanup(e.target.value)}
              rows={2}
              className={editHidden.has("cleanup") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            {renderSectionLabel("completion", t("pages.contracts.detail.editCompletionLabel"))}
            <Textarea
              value={editCompletion}
              onChange={(e) => setEditCompletion(e.target.value)}
              rows={2}
              className={editHidden.has("completion") ? "opacity-60" : ""}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            {renderSectionLabel("agreement", t("pages.contracts.detail.editAgreementLabel"))}
            <Textarea
              value={editAgreement}
              onChange={(e) => setEditAgreement(e.target.value)}
              rows={2}
              className={editHidden.has("agreement") ? "opacity-60" : ""}
            />
          </div>
        </div>
      ) : (
        <>
          {contract.scope && (
            <div
              className={`rounded-lg border border-border bg-card p-5 ${
                isSectionHidden(contract.hidden_sections, "scope") ? "opacity-60" : ""
              }`}
            >
              <div className="mb-2 flex items-center gap-2">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.contracts.detail.scope")}
                </p>
                {isSectionHidden(contract.hidden_sections, "scope") && (
                  <Badge variant="outline" className="gap-1 text-[10px]">
                    <EyeOff className="h-3 w-3" />
                    {t("pages.contracts.detail.hiddenBadge")}
                  </Badge>
                )}
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{contract.scope}</p>
            </div>
          )}

          {contract.terms && (
            <div
              className={`rounded-lg border border-border bg-card p-5 ${
                isSectionHidden(contract.hidden_sections, "payment") ? "opacity-60" : ""
              }`}
            >
              <div className="mb-2 flex items-center gap-2">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.contracts.detail.terms")}
                </p>
                {isSectionHidden(contract.hidden_sections, "payment") && (
                  <Badge variant="outline" className="gap-1 text-[10px]">
                    <EyeOff className="h-3 w-3" />
                    {t("pages.contracts.detail.hiddenBadge")}
                  </Badge>
                )}
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
                {contract.terms}
              </p>
            </div>
          )}
        </>
      )}

      <div className="grid gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-2">
        <SignatureBlock
          label={t("pages.contracts.detail.client")}
          name={contract.client_signature_name}
          email={contract.client_signature_email}
          signedAt={contract.signed_at}
          pendingLabel={t("pages.contracts.detail.pendingSignature")}
          signedAtLabel={(d) => t("pages.contracts.detail.signedAt", { date: d })}
        />
        <SignatureBlock
          label={t("pages.contracts.detail.company")}
          name={contract.company_signature_name}
          email={null}
          signedAt={contract.signed_at}
          pendingLabel={t("pages.contracts.detail.pendingSignature")}
          signedAtLabel={(d) => t("pages.contracts.detail.signedAt", { date: d })}
        />
      </div>

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pages.contracts.detail.signDialogTitle")}</DialogTitle>
            <DialogDescription>{t("pages.contracts.detail.signDialogDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("pages.contracts.detail.signerName")}</Label>
              <Input value={signName} onChange={(e) => setSignName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.contracts.detail.signerEmail")}</Label>
              <Input
                type="email"
                value={signEmail}
                onChange={(e) => setSignEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)} disabled={signing}>
              {t("common.cancel")}
            </Button>
            <Button onClick={sign} disabled={signing}>
              {signing
                ? t("pages.contracts.detail.signing")
                : t("pages.contracts.detail.confirmSignature")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.contracts.detail.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription
              dangerouslySetInnerHTML={{
                __html: t("pages.contracts.detail.deleteDesc", { number: contract.number }),
              }}
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pages.contracts.detail.sendDialogTitle")}</DialogTitle>
            <DialogDescription>{t("pages.contracts.detail.sendDialogDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="contract-send-email">
                {t("pages.contracts.detail.recipientEmail")}
              </Label>
              <Input
                id="contract-send-email"
                type="email"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contract-send-message">
                {t("pages.contracts.detail.messageOptional")}
              </Label>
              <Textarea
                id="contract-send-message"
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                rows={3}
                placeholder={t("pages.contracts.detail.sendDialogPlaceholder")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>
              {t("common.cancel")}
            </Button>
            <Button onClick={sendApprovalLink} disabled={sending} className="gap-2">
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sending ? t("pages.contracts.detail.sending") : t("pages.contracts.detail.sendLink")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.contracts.detail.saveChanges")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("pages.contracts.detail.editingWarn", {
                status: t(`statuses.${contract.status}`),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingEdit}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => performSaveEdit(true)} disabled={savingEdit}>
              {savingEdit
                ? t("pages.contracts.detail.savingDots")
                : t("pages.contracts.detail.saveChanges")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={pdfPreviewOpen}
        onOpenChange={(open) => (open ? setPdfPreviewOpen(true) : closePdfPreview())}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t("pages.contracts.detail.pdfPreviewTitle")}</DialogTitle>
            <DialogDescription>
              {t("pages.contracts.detail.pdfPreviewDesc", { filename: pdfPreviewFilename })}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[70vh] w-full overflow-hidden rounded-md border border-border bg-muted">
            {pdfPreviewBase64 ? (
              <PdfCanvasPreview pdfBase64={pdfPreviewBase64} />
            ) : (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePdfPreview}>
              {t("common.close")}
            </Button>
            <Button onClick={confirmDownloadPdf} disabled={!pdfPreviewBase64} className="gap-2">
              <FileDown className="h-4 w-4" />
              {t("pages.contracts.detail.downloadPdf")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <p className="mt-6 text-center text-[11px] italic text-muted-foreground">
        {t("pages.contracts.detail.footerNote")}
      </p>
    </div>
  );
}

function SignatureBlock({
  label,
  name,
  email,
  signedAt,
  pendingLabel,
  signedAtLabel,
}: {
  label: string;
  name: string | null;
  email: string | null;
  signedAt: string | null;
  pendingLabel: string;
  signedAtLabel: (date: string) => string;
}) {
  return (
    <div className="rounded-md border border-dashed border-border p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      {name ? (
        <>
          <p className="mt-1 font-serif text-xl italic">{name}</p>
          {email && <p className="text-xs text-muted-foreground">{email}</p>}
          {signedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              {signedAtLabel(new Date(signedAt).toLocaleString())}
            </p>
          )}
        </>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">{pendingLabel}</p>
      )}
    </div>
  );
}
